import * as React from 'react';
import { useQuery } from '@apollo/client';
import { AuthContext } from 'auth/dist/contexts';
import { useHistory } from 'react-router';

import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import * as ProgramSeasonsDivisions from 'core/gql/queries/program-season-divisions';

const cookieExpiration = 86400; // 24 hours

export default function useShowMerchantAccountWarning(orgId: number) {
  const [showWarning, setShowWarning] = React.useState(false);

  const history = useHistory();

  const context = React.useContext(AuthContext);
  const user = context.user;

  const cookieName = `merchant-account-warning-${orgId}`;
  const cookieValue = user.id;

  const listPaymentAccountsData = useQuery(ListPaymentAccounts.QUERY, {
    variables: { scopeId: +orgId, scopeType: 'organization' },
    errorPolicy: 'all',
  });

  const programSeasonsDivisionsData = useQuery(ProgramSeasonsDivisions.QUERY, {
    variables: { organizationId: +orgId },
    errorPolicy: 'all',
  });

  const closeWarning = () => {
    document.cookie = `${cookieName}=${cookieValue};max-age=${cookieExpiration};path=/`;
    setShowWarning(false);
  };

  const goToMerchantAccounts = () => {
    history.push(`/organizations/${orgId}/settings/merchant-accounts`);
    closeWarning();
  };

  const [numOfInvalidPaymentsEnabled, numOfInvalidPayoutsEnabled, numOfInvalidCADAccounts] = React.useMemo(() => {
    let numOfInvalidPaymentsEnabled = 0;
    let numOfInvalidPayoutsEnabled = 0;
    let numOfInvalidCADAccounts = 0;
    if (listPaymentAccountsData.data && programSeasonsDivisionsData.data) {
      const merchantAccounts = listPaymentAccountsData.data.listPaymentAccounts;
      const programs = programSeasonsDivisionsData.data.programs;
      const seasons = programs?.flatMap((p) => p.seasons).filter((s) => s.defaultPaymentAccountId);
      seasons?.forEach((s) => {
        const merchantAccount = merchantAccounts?.find((m) => +m.id === +s?.defaultPaymentAccountId);
        if (!merchantAccount || s.isArchived) { return; }
        if (!merchantAccount.paymentsEnabled) {
          numOfInvalidPaymentsEnabled++;
          return;
        }
        if (!merchantAccount.payoutsEnabled && merchantAccount.currency?.toUpperCase() === 'CAD') {
          numOfInvalidCADAccounts++;
        }
        if (!merchantAccount.payoutsEnabled) {
          numOfInvalidPayoutsEnabled++;
        }
      });
    }

    return [numOfInvalidPaymentsEnabled, numOfInvalidPayoutsEnabled, numOfInvalidCADAccounts];
  }, [listPaymentAccountsData, programSeasonsDivisionsData]);

  React.useEffect(() => {
    if (user && listPaymentAccountsData.data && programSeasonsDivisionsData.data) {
      const cookieValue = document.cookie
        ?.split(';')
        ?.find((item) => item.trim().startsWith(`${cookieName}=`))
        ?.split('=')
        .pop();

      if ((!cookieValue || (cookieValue && +cookieValue !== +user.id)) &&
        (numOfInvalidPaymentsEnabled > 0 || numOfInvalidPayoutsEnabled > 0)) {
        setShowWarning(true);
      }
    }
  }, [cookieName, user, listPaymentAccountsData, programSeasonsDivisionsData,
      numOfInvalidPaymentsEnabled, numOfInvalidPayoutsEnabled]);

  return {
    numOfInvalidPaymentsEnabled: numOfInvalidPaymentsEnabled,
    numOfInvalidPayoutsEnabled: numOfInvalidPayoutsEnabled,
    showWarning,
    closeWarning,
    goToMerchantAccounts,
    showCADWarning: numOfInvalidCADAccounts > 0,
  };
}
